import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useStore } from "@/store/useStore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Medal, 
  Trophy, 
  Crown,
  User,
  Loader2
} from "lucide-react";

// League helper function
const getLeagueForRating = (rating) => {
  const leagues = [
    { name: "Legend", min: 2400, icon: "⭐", color: "text-orange-500" },
    { name: "Grandmaster", min: 2200, icon: "🏆", color: "text-red-500" },
    { name: "Master", min: 2000, icon: "👑", color: "text-purple-500" },
    { name: "Diamond", min: 1800, icon: "💠", color: "text-blue-400" },
    { name: "Platinum", min: 1600, icon: "💎", color: "text-cyan-300" },
    { name: "Gold", min: 1400, icon: "🥇", color: "text-yellow-500" },
    { name: "Silver", min: 1200, icon: "🥈", color: "text-gray-400" },
    { name: "Bronze", min: 0, icon: "🥉", color: "text-amber-600" },
  ];
  
  for (const league of leagues) {
    if (rating >= league.min) {
      return league;
    }
  }
  return leagues[leagues.length - 1];
};

export default function LeaderboardPage() {
  const { t } = useTranslation();
  const { user, fetchLeaderboard } = useStore();
  
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    setLoading(true);
    const data = await fetchLeaderboard();
    setPlayers(data);
    setLoading(false);
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="h-6 w-6 text-yellow-500" />;
      case 2:
        return <Medal className="h-6 w-6 text-gray-400" />;
      case 3:
        return <Medal className="h-6 w-6 text-amber-700" />;
      default:
        return <span className="text-lg font-bold text-muted-foreground w-6 text-center">{rank}</span>;
    }
  };

  const getRankClass = (rank) => {
    switch (rank) {
      case 1:
        return "rank-1";
      case 2:
        return "rank-2";
      case 3:
        return "rank-3";
      default:
        return "";
    }
  };

  return (
    <div className="space-y-8" data-testid="leaderboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-black tracking-tight flex items-center gap-3">
          <Trophy className="h-8 w-8 text-primary" />
          {t("nav.leaderboard")}
        </h1>
        <p className="text-muted-foreground mt-2">
          Top rated players on the platform
        </p>
      </div>

      <Card className="glass border-border/50">
        <CardHeader>
          <CardTitle className="text-lg">Top 100 Players</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : players.length === 0 ? (
            <div className="text-center py-20">
              <Trophy className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("common.noResults")}</h3>
              <p className="text-muted-foreground">
                No players yet. Be the first to play!
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[600px]">
              <div className="space-y-2">
                {/* Header */}
                <div className="grid grid-cols-12 gap-4 px-4 py-2 text-sm text-muted-foreground border-b border-border/50">
                  <div className="col-span-1">Rank</div>
                  <div className="col-span-5">Player</div>
                  <div className="col-span-2 text-center">Rating</div>
                  <div className="col-span-2 text-center">Games</div>
                  <div className="col-span-2 text-center">Win Rate</div>
                </div>

                {/* Players list */}
                {players.map((player, index) => {
                  const rank = index + 1;
                  const winRate = player.games_played > 0 
                    ? ((player.games_won / player.games_played) * 100).toFixed(1)
                    : "0.0";
                  const isCurrentUser = player.id === user?.id;

                  return (
                    <div 
                      key={player.id}
                      className={`grid grid-cols-12 gap-4 items-center px-4 py-3 rounded-lg transition-colors
                        ${isCurrentUser ? "bg-primary/10 border border-primary/30" : "hover:bg-muted/50"}
                        ${rank <= 3 ? "bg-muted/30" : ""}
                      `}
                      data-testid={`leaderboard-player-${player.id}`}
                    >
                      <div className="col-span-1 flex items-center">
                        {getRankIcon(rank)}
                      </div>
                      
                      <div className="col-span-5 flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                          rank <= 3 ? "bg-primary/20" : "bg-muted"
                        }`}>
                          <User className={`h-5 w-5 ${rank <= 3 ? "text-primary" : "text-muted-foreground"}`} />
                        </div>
                        <div>
                          <p className={`font-semibold ${getRankClass(rank)}`}>
                            {player.username}
                            {isCurrentUser && <span className="text-primary ml-2">(You)</span>}
                          </p>
                          <span className={`text-xs ${getLeagueForRating(player.rating).color}`}>
                            {getLeagueForRating(player.rating).icon} {getLeagueForRating(player.rating).name}
                          </span>
                        </div>
                      </div>
                      
                      <div className="col-span-2 text-center">
                        <span className="font-accent text-lg font-bold text-primary">
                          {player.rating}
                        </span>
                      </div>
                      
                      <div className="col-span-2 text-center text-muted-foreground">
                        {player.games_played}
                      </div>
                      
                      <div className="col-span-2 text-center">
                        <span className={`font-medium ${
                          parseFloat(winRate) >= 60 ? "text-green-500" : 
                          parseFloat(winRate) >= 40 ? "text-yellow-500" : 
                          "text-red-500"
                        }`}>
                          {winRate}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
